import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  ArrowRight,
  User,
  Mountain,
  Landmark,
  Cpu,
  Scale,
  ShieldCheck,
  Database,
  Boxes,
  Sparkles,
} from 'lucide-react';
import { colors } from '../styles/colors';

const SectionHeading = ({ eyebrow, title, sub, align = 'center', maxW = 'max-w-3xl' }) => (
  <div className={`${maxW} ${align === 'center' ? 'mx-auto text-center' : ''} mb-10 sm:mb-12`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>{eyebrow}</span>
        {align === 'center' && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
      </div>
    )}
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h2>
    {sub && <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{sub}</p>}
  </div>
);

// §2 mission progression steps
const missionFlow = [
  { label: 'Asset', Icon: Mountain },
  { label: 'Verification', Icon: ShieldCheck },
  { label: 'Reserve', Icon: Database },
  { label: 'Utility', Icon: Boxes },
];

// §3 leadership (internal)
const leadership = [
  { name: 'Lee Oakey', role: 'Chief Executive Officer', image: '/lee-oakey.webp', focal: 'center 34%', zoom: 1.0, linkedin: 'https://www.linkedin.com/in/leeoakey/', desc: 'Responsible for Arizore\u2019s overall strategy, commercial direction and development of the company\u2019s global ecosystem.' },
  { name: 'George Boot', role: 'Chief Marketing Officer', image: '/george-boot.webp', focal: 'center 1%', zoom: 1.95, linkedin: 'https://www.linkedin.com/in/george-boot95/', desc: 'Leads Arizore\u2019s global brand, market positioning, institutional communications, partnerships and go-to-market strategy.' },
  { name: 'John Smith', role: 'Chief Commercial Officer', image: '/john-smith.webp', focal: 'center 20%', zoom: 1.0, desc: 'Responsible for revenue strategy, commercial growth and developing Arizore\u2019s global asset and capital relationships.' },
  { name: 'Dean Foreman', role: 'Chief Financial Officer', image: '/dean-foreman.webp', focal: 'center 22%', zoom: 1.0, desc: 'Oversees financial strategy, governance, capital planning and financial operations across the Arizore ecosystem.' },
  { name: 'Giorgio Forgione', role: 'Head of Account Management', image: '/giorgio-forgione.webp', focal: 'center 32%', zoom: 1.12, linkedin: 'https://www.linkedin.com/in/giorgio-forgione/', desc: 'Leads relationships with asset owners and manages the progression of opportunities through Arizore\u2019s assessment and onboarding process.' },
];

// §4 specialist network (external partners / advisors — not employees)
const specialists = [
  { name: 'Ricky Phoon', role: 'Strategic Partnerships Advisor for APAC', image: '/ricky-phoon.webp', focal: 'center 20%', zoom: 1.0, linkedin: 'https://www.linkedin.com/in/ricky-phoon/', desc: 'Supports strategic relationships, market development and partnership opportunities across the Asia-Pacific region.' },
  { name: 'Caroline Macdonald', role: 'Tokenisation Partner \u2014 Blacktie Digital', image: '/caroline-macdonald.webp', focal: 'center 28%', zoom: 1.0, linkedin: 'https://www.linkedin.com/in/carolinmacdonald/', desc: 'Supports Arizore\u2019s tokenisation infrastructure and the digital issuance framework underpinning the Arizore ecosystem.' },
  { name: 'Josh Payne', role: 'Mining Technical Lead & Asset Review Partner', image: '/josh-payne.webp', focal: 'center 24%', zoom: 1.0, linkedin: 'https://www.linkedin.com/in/joshpaynetech/', desc: 'Provides technical mining expertise and supports the review and assessment of underlying mineral assets entering the Arizore ecosystem.' },
];

// §5 disciplines
const disciplines = [
  { Icon: Mountain, title: 'Asset & Technical', body: 'Mining expertise, geological information and asset assessment.' },
  { Icon: Landmark, title: 'Commercial & Capital', body: 'Asset relationships, institutional capital and commercial structuring.' },
  { Icon: Cpu, title: 'Technology & Tokenisation', body: 'Digital infrastructure designed to create transparency, programmability and utility around verified assets.' },
  { Icon: Scale, title: 'Governance & Market Development', body: 'Financial oversight, communications, partnerships and ecosystem development.' },
];

// Reusable profile card — placeholder-first, built so a standardised portrait can drop in later via `image`.
const ProfileCard = ({ name, role, desc, image, focal = 'center 22%', zoom = 1, linkedin, variant = 'leadership', testid }) => {
  const isSpecialist = variant === 'specialist';
  const accent = isSpecialist ? 'rgba(255,255,255,0.35)' : colors.teal;
  return (
    <div
      data-testid={testid}
      className="rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-500"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)',
        border: `1px solid ${isSpecialist ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.08)'}`,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = isSpecialist ? 'rgba(255,255,255,0.25)' : `${colors.teal}45`; e.currentTarget.style.transform = 'translateY(-3px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = isSpecialist ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Unified portrait container — identical 4:5 head-and-shoulders frame for every card.
          Source dimensions never dictate the visible crop: object-fit cover + per-person focal/zoom. */}
      <div
        className="relative w-full overflow-hidden flex items-center justify-center"
        style={{
          aspectRatio: '4 / 5',
          background: isSpecialist
            ? 'linear-gradient(180deg, #16181A 0%, #0C0E10 100%)'
            : 'linear-gradient(180deg, #14171A 0%, #0A0C0E 100%)',
          borderBottom: `1px solid ${isSpecialist ? 'rgba(255,255,255,0.07)' : `${colors.teal}22`}`,
        }}
      >
        {image ? (
          <>
            <img
              src={image}
              alt={name}
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                objectPosition: focal,
                transform: `scale(${zoom})`,
                transformOrigin: focal,
                filter: 'grayscale(100%) contrast(1.03) brightness(0.96)',
              }}
              loading="lazy"
            />
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(5,6,7,0.5) 100%), radial-gradient(70% 60% at 50% 40%, ${isSpecialist ? 'rgba(200,152,32,0.05)' : `${colors.teal}0f`} 0%, rgba(0,0,0,0) 78%)` }} />
          </>
        ) : (
          <>
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(60% 55% at 50% 42%, ${isSpecialist ? 'rgba(200,152,32,0.06)' : `${colors.teal}14`} 0%, rgba(0,0,0,0) 72%)` }} />
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: `1px solid ${isSpecialist ? 'rgba(255,255,255,0.14)' : `${colors.teal}33`}` }}>
              <User className="w-6 h-6" strokeWidth={1.25} style={{ color: isSpecialist ? 'rgba(255,255,255,0.4)' : colors.teal }} />
            </div>
          </>
        )}
        {isSpecialist && (
          <span className="absolute top-2.5 left-2.5 text-[8px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.14)' }}>
            Partner
          </span>
        )}
      </div>

      {/* Details */}
      <div className="px-4 py-4 flex flex-col flex-grow">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <h3 className="text-[15px] font-semibold tracking-tight" style={{ color: colors.white }}>{name}</h3>
          {linkedin && (
            <a
              href={linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View ${name} on LinkedIn`}
              data-testid={`${testid}-linkedin`}
              className="shrink-0 inline-flex items-center justify-center transition-opacity duration-200"
              style={{ width: '22px', height: '22px', opacity: 0.9 }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.9'; }}
            >
              <img src="/linkedin-icon.png" alt="" aria-hidden="true" className="w-full h-full" style={{ display: 'block' }} />
            </a>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-[0.12em] font-medium mb-2" style={{ color: accent }}>{role}</span>
        <p className="text-[13px] leading-relaxed" style={{ color: colors.white, opacity: 0.6 }}>{desc}</p>
      </div>
    </div>
  );
};

const CompanyPage = () => {
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setHeroVisible(true));
    window.scrollTo(0, 0);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }} data-testid="company-page">
      {/* §1 HERO — compact, atmospheric globe background */}
      <section className="relative overflow-hidden" style={{ minHeight: '62vh', background: 'linear-gradient(180deg, #000000 0%, #03090C 60%, #07141A 100%)' }}>
        <img
          src="/company-hero.webp"
          alt=""
          aria-hidden="true"
          data-testid="company-hero-image"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`}
          style={{ objectPosition: 'center center' }}
          loading="eager"
          decoding="async"
        />
        {/* Dark Arizore overlays — keep network recognisable but subordinate to headline */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.5) 42%, rgba(0,0,0,0.68) 74%, rgba(5,5,5,0.98) 100%)' }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(48% 46% at 50% 48%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0) 78%)' }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 65% at 50% 50%, rgba(0,128,128,0.07) 0%, rgba(0,0,0,0) 70%)' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center min-h-[62vh] pt-32 pb-20 sm:pt-36 sm:pb-24">
          <div className={`transition-all duration-1000 ease-out transform ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex items-center gap-3 mb-6 justify-center">
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-medium" style={{ color: colors.teal }}>
                <Building2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                Company
              </span>
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-[-0.02em] mb-6 max-w-3xl mx-auto" style={{ color: colors.white, fontWeight: 700 }}>
              Built around the asset. Operated by people.
            </h1>
            <p className="text-base sm:text-lg leading-[1.6] max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Arizore brings together mining expertise, institutional infrastructure, technology and commercial leadership to build a more transparent and productive framework around real-world mineral assets.
            </p>
          </div>
        </div>
      </section>

      {/* §2 OUR MISSION */}
      <section className="relative py-14 sm:py-20 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(55% 55% at 50% 35%, rgba(0,128,128,0.07) 0%, rgba(0,0,0,0) 70%)' }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            title="Our Mission"
            sub="To build trusted infrastructure that connects verified mineral assets with modern financial markets — giving asset owners new ways to unlock value while creating transparent, asset-backed opportunities globally."
          />
          {/* Understated progression: Asset → Verification → Reserve → Utility */}
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-4 sm:gap-x-4" data-testid="company-mission-flow">
            {missionFlow.map(({ label, Icon }, i) => (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center gap-2.5">
                  <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}33` }}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={1.5} style={{ color: colors.teal }} />
                  </span>
                  <span className="text-xs sm:text-sm font-medium tracking-tight" style={{ color: colors.white, opacity: 0.85 }}>{label}</span>
                </div>
                {i < missionFlow.length - 1 && (
                  <ArrowRight className="w-4 h-4 mb-6" strokeWidth={1.5} style={{ color: colors.white, opacity: 0.3 }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* §3 LEADERSHIP */}
      <section className="relative py-14 sm:py-20 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.08 }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            title="Leadership"
            sub="The team responsible for Arizore's strategy, growth, financial management, commercial development and asset relationships."
          />
          {/* 3 + 2 symmetric, centred */}
          <div className="flex flex-wrap justify-center gap-4 lg:gap-5">
            {leadership.map((p) => (
              <div key={p.name} className="w-[calc(50%-0.5rem)] sm:w-[240px]">
                <ProfileCard {...p} variant="leadership" testid={`company-leader-${p.name.toLowerCase().replace(/\s+/g, '-')}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §4 SPECIALIST NETWORK — organisationally distinct from leadership */}
      <section className="relative py-14 sm:py-20 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-nodes.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.07 }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="External Expertise"
            title="Specialist Network"
            sub="Arizore works alongside specialist partners and advisors across mining, tokenisation, technology and regional market development, bringing dedicated expertise into the ecosystem where required."
          />
          <div className="flex flex-wrap justify-center gap-4 lg:gap-5">
            {specialists.map((p) => (
              <div key={p.name} className="w-full sm:w-[248px]">
                <ProfileCard {...p} variant="specialist" testid={`company-specialist-${p.name.toLowerCase().replace(/\s+/g, '-')}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §5 HOW WE OPERATE */}
      <section className="relative py-14 sm:py-20 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.09 }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="How We Operate"
            title="One operating framework."
            sub="The disciplines Arizore brings together to build a transparent, asset-first framework around verified mineral assets."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-12">
            {disciplines.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`company-discipline-${title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                className="p-7 rounded-2xl h-full flex flex-col transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}40`; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold mb-2 tracking-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
              </div>
            ))}
          </div>
          <p className="max-w-2xl mx-auto text-center text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.7 }}>
            Arizore coordinates these disciplines through a single operating framework while working with specialist partners where dedicated expertise is required.
          </p>
        </div>
      </section>

      {/* §6 CLOSING CTA */}
      <section className="relative py-14 sm:py-20 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(55% 60% at 50% 50%, rgba(0,128,128,0.1) 0%, rgba(0,0,0,0) 70%)' }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
              The Road Ahead
            </span>
            <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5 tracking-tight leading-tight" style={{ color: colors.white }}>
            Building the infrastructure for the next generation of mineral assets.
          </h2>
          <p className="max-w-2xl mx-auto mb-10 text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.7 }}>
            Arizore is developing an ecosystem designed to connect asset owners, specialist operators and financial markets through a transparent, asset-first framework.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link
              to="/reserve-architecture"
              data-testid="company-cta-explore"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300 w-full sm:w-auto"
              style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}`, textDecoration: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Explore Arizore
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/mines"
              data-testid="company-cta-asset-owners"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300 w-full sm:w-auto"
              style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)', textDecoration: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            >
              For Asset Owners
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyPage;
